import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import type {
  Tier,
  TierProgramCell,
  TierProgramDetail,
  TierProgramMatrixRow,
} from "@colab/shared";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { useAutosave, type SaveStatus } from "@/hooks/use-autosave";
import { queryKeys } from "@/lib/query-keys";
import { formatApiError } from "@/lib/format-api-error";
import { cn } from "@/lib/utils";
import { useDeleteTierRow } from "./use-delete-row";
import { useSetTierCell } from "./use-set-cell";
import { useUpdateTierRow } from "./use-update-row";

// Sortable matrix row. Three editable surfaces:
//
//   1. Row label — debounced autosave via useAutosave (500ms). Critical
//      correctness point: the onFlush callback re-reads the program-detail
//      cache via queryClient.getQueryData() and patches the matching row in
//      place, never closing over the editor's local state. Per-cell
//      optimistic writes that landed during the typing debounce window stay
//      intact. Sub-bucket 11 surfaced this exact lesson.
//
//   2. Per-cell — on-blur via useSetTierCell. Pattern C optimistic on
//      tiers.program(programId). No debounce, matches legacy.
//
//   3. Row delete — destructive AlertDialog confirm. Cells cascade via FK ON
//      DELETE CASCADE on the server.

export function TierMatrixRow({
  programId,
  row,
  tiers,
  cellMap,
  canDrag,
  gridTemplate,
  onSaveStatusChange,
}: {
  programId: string;
  row: TierProgramMatrixRow;
  tiers: Tier[];
  cellMap: Map<string, TierProgramCell>;
  canDrag: boolean;
  gridTemplate: string;
  onSaveStatusChange: (status: SaveStatus) => void;
}) {
  const queryClient = useQueryClient();
  const updateRow = useUpdateTierRow();
  const deleteRow = useDeleteTierRow();

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: row.id, disabled: !canDrag });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const [label, setLabel] = useState(row.label);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    setLabel(row.label);
  }, [row.label]);

  // Mutator pattern: re-read the cache at flush time so we never clobber
  // per-cell optimistic writes that landed during the 500ms typing window.
  const flushLabel = useCallback(
    async (nextLabel: string) => {
      const trimmed = nextLabel.trim();
      if (!trimmed) return;
      const key = queryKeys.tiers.program(programId);
      const cached = queryClient.getQueryData<TierProgramDetail>(key);
      const currentLabel =
        cached?.rows.find((r) => r.id === row.id)?.label ?? row.label;
      if (trimmed === currentLabel) return;
      await updateRow.mutateAsync({
        programId,
        id: row.id,
        patch: { label: trimmed },
      });
    },
    [programId, queryClient, row.id, row.label, updateRow],
  );

  const autosave = useAutosave<string>({ onFlush: flushLabel, wait: 500 });

  useEffect(() => {
    onSaveStatusChange(autosave.status);
  }, [autosave.status, onSaveStatusChange]);

  function onLabelChange(v: string) {
    setLabel(v);
    autosave.queue(v);
  }

  function handleDeleteConfirm() {
    deleteRow.mutate(
      { programId, id: row.id },
      {
        onSuccess: () => {
          toast.success("Row deleted");
          setDeleteOpen(false);
        },
        onError: (err) => {
          toast.error(formatApiError(err, "Failed to delete row"));
          setDeleteOpen(false);
        },
      },
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "border-b last:border-b-0 transition-colors",
        isDragging && "z-50 bg-card opacity-80 shadow-lg",
      )}
    >
      <div
        className="grid items-center divide-x"
        style={{ gridTemplateColumns: gridTemplate }}
      >
        <div className="flex items-center gap-1.5 px-2 py-2">
          <button
            type="button"
            aria-label="Drag to reorder row"
            {...attributes}
            {...listeners}
            disabled={!canDrag}
            className="cursor-grab text-muted-foreground/60 transition hover:text-foreground active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-50"
          >
            <GripVertical className="h-3.5 w-3.5" />
          </button>
          <Input
            value={label}
            onChange={(e) => onLabelChange(e.target.value)}
            onBlur={() => autosave.flush()}
            className="h-7 border-transparent bg-transparent px-1.5 text-sm font-medium shadow-none hover:border-input focus-visible:border-input focus-visible:bg-background"
            maxLength={160}
            placeholder="Row label"
          />
        </div>

        {tiers.map((t) => {
          const cell = cellMap.get(`${row.id}:${t.id}`);
          return (
            <CellInput
              key={t.id}
              rowId={row.id}
              tierId={t.id}
              programId={programId}
              initial={cell?.value ?? ""}
            />
          );
        })}

        <div className="flex items-center justify-end px-2">
          <button
            type="button"
            onClick={() => setDeleteOpen(true)}
            disabled={deleteRow.isPending}
            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
            aria-label={`Delete row ${row.label}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this row?</AlertDialogTitle>
            <AlertDialogDescription>
              All cells in &quot;{row.label}&quot; will be removed. This
              can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteRow.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteRow.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteRow.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete row
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CellInput({
  rowId,
  tierId,
  programId,
  initial,
}: {
  rowId: string;
  tierId: string;
  programId: string;
  initial: string;
}) {
  const setCell = useSetTierCell();
  const [value, setValue] = useState(initial);

  // Re-sync from server / cache when initial changes (e.g. another tab
  // commits a cell value for this row+tier pair via cross-key invalidate).
  useEffect(() => {
    setValue(initial);
  }, [initial]);

  function commit() {
    if (value === initial) return;
    setCell.mutate({
      programId,
      input: {
        row_id: rowId,
        tier_id: tierId,
        value: value.trim() === "" ? null : value,
      },
    });
  }

  return (
    <div className="px-2 py-2">
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          if (e.key === "Escape") {
            setValue(initial);
            (e.target as HTMLInputElement).blur();
          }
        }}
        maxLength={500}
        placeholder="—"
        className="h-7 w-full rounded-md border border-transparent bg-transparent px-1.5 text-center text-sm tabular-nums outline-none transition-colors hover:border-input focus:border-input focus:bg-background"
      />
    </div>
  );
}
